import React, { useState } from 'react';
import { X } from 'lucide-react';
import { apiRequest } from '../../config/api';
import { useToast } from '../../context/ToastContext';

function slugify(name) {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s_]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

export default function CreateObjectDefinitionModal({ onClose, onCreated }) {
  const { showSuccess, showError } = useToast();
  const [form, setForm] = useState({ name: '', slug: '', slug_plural: '', icon: 'box' });
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugPluralTouched, setSlugPluralTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleNameChange = (name) => {
    const slug = slugTouched ? undefined : slugify(name);
    const slugPlural = slugPluralTouched ? undefined : (slug ? slug + 's' : slugify(name) + 's');
    setForm((prev) => ({
      ...prev,
      name,
      ...(slug !== undefined && { slug }),
      ...(slugPlural !== undefined && { slug_plural: slugPlural }),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await apiRequest('/crm/objects/', {
        method: 'POST',
        body: JSON.stringify(form),
      });

      if (response.ok) {
        const newObj = await response.json();
        showSuccess(`"${newObj.name}" created`);
        onCreated(newObj);
      } else {
        const err = await response.json();
        showError(err.detail || 'Failed to create object');
      }
    } catch (error) {
      showError('Error creating object');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onClose} />
      <div className="relative z-10 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            New Object
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Name
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value)}
              className="block w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors"
              placeholder="e.g. Product, Ticket, Project"
              required
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Slug
            </label>
            <input
              type="text"
              value={form.slug}
              onChange={(e) => {
                setSlugTouched(true);
                setForm((prev) => ({ ...prev, slug: slugify(e.target.value) }));
              }}
              className="block w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 font-mono transition-colors"
              placeholder="auto_generated"
              required
            />
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Unique identifier — cannot be changed later
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Plural Slug
            </label>
            <input
              type="text"
              value={form.slug_plural}
              onChange={(e) => {
                setSlugPluralTouched(true);
                setForm((prev) => ({ ...prev, slug_plural: slugify(e.target.value) }));
              }}
              className="block w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 font-mono transition-colors"
              placeholder="e.g. companies, people"
            />
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Used in URLs: /data/{form.slug_plural || '...'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Icon
            </label>
            <input
              type="text"
              value={form.icon}
              onChange={(e) => setForm((prev) => ({ ...prev, icon: e.target.value }))}
              className="block w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors"
              placeholder="e.g. box, package, tag"
            />
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Lucide icon name
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !form.name.trim() || !form.slug.trim()}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Creating...' : 'Create Object'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
